import * as React from "react";
import { Clock } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useIsMobile } from "@/hooks/useMobile";

interface TimePickerProps {
  value: string;
  onChange: (value: string) => void;
  className?: string;
  disabled?: boolean;
}

export function TimePicker({ value, onChange, className, disabled }: TimePickerProps) {
  const [open, setOpen] = React.useState(false);
  const hoursRef = React.useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();
  
  const hours = Array.from({ length: 15 }, (_, i) => i + 6); // 6h to 20h
  const minutes = ["00", "15", "30", "45"];
  
  const [selectedHour, selectedMinute] = value ? value.split(":") : ["08", "00"];
  
  // Auto-scroll to selected hour when popover opens
  React.useEffect(() => {
    if (open && hoursRef.current) {
      // Small delay to ensure popover is rendered
      setTimeout(() => {
        const selectedButton = hoursRef.current?.querySelector(`[data-hour="${selectedHour}"]`);
        if (selectedButton) {
          selectedButton.scrollIntoView({ block: "center", behavior: "instant" });
        }
      }, 10);
    }
  }, [open, selectedHour]);
  
  const handleTimeSelect = (hour: string, minute: string) => {
    onChange(`${hour}:${minute}`);
    setOpen(false);
  };
  
  const formatDisplayTime = (time: string) => {
    if (!time) return "Sélectionner";
    const [h, m] = time.split(":");
    return `${h}h${m}`;
  };

  // Native time input for mobile
  if (isMobile) {
    return (
      <div className={cn("relative", className)}>
        <input
          type="time"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className={cn(
            "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-body-md transition-colors",
            "file:border-0 file:bg-transparent file:text-body-md file:font-medium",
            "placeholder:text-muted-foreground",
            "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
            "disabled:cursor-not-allowed disabled:opacity-50"
          )}
        />
      </div>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "w-full justify-start text-left font-medium",
            !value && "text-muted-foreground",
            className
          )}
        >
          <Clock className="mr-2 h-4 w-4" />
          {formatDisplayTime(value)}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <div className="flex">
          {/* Hours column */}
          <div className="w-16 border-r flex flex-col">
            <p className="text-body-sm text-muted-foreground py-2 text-center bg-popover border-b shrink-0">Heure</p>
            <div 
              ref={hoursRef} 
              className="overflow-y-auto overscroll-contain p-1"
              style={{ height: '220px', WebkitOverflowScrolling: 'touch' }}
            >
              {hours.map((hour) => {
                const hourStr = hour.toString().padStart(2, "0");
                return (
                  <Button
                    key={hour}
                    data-hour={hourStr}
                    variant={selectedHour === hourStr ? "default" : "ghost"}
                    size="sm"
                    className="w-full mb-0.5 h-9"
                    onClick={() => handleTimeSelect(hourStr, selectedMinute)}
                  >
                    {hourStr}h
                  </Button>
                );
              })}
            </div>
          </div>
          {/* Minutes column */}
          <div className="w-16 flex flex-col">
            <p className="text-body-sm text-muted-foreground py-2 text-center bg-popover border-b shrink-0">Min</p>
            <div 
              className="overflow-y-auto overscroll-contain p-1"
              style={{ height: '220px', WebkitOverflowScrolling: 'touch' }}
            >
              {minutes.map((minute) => (
                <Button
                  key={minute}
                  variant={selectedMinute === minute ? "default" : "ghost"}
                  size="sm"
                  className="w-full mb-0.5 h-9"
                  onClick={() => handleTimeSelect(selectedHour, minute)}
                >
                  {minute}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
